import { IconChevronDown, IconMessageCircleStar, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { getApi, postApi } from "../../../api/call.api";
import { urlApi } from "../../../api/url.api";
import { loadingHide, loadingShow } from "../../../loadingUi/loading.controller";

const AssignChatPlanModal = ({ userData }) => {
    const [show, setShow] = useState(false);
    const [billingType, setBillingType] = useState("monthly"); // monthly | yearly
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planList, setPlanList] = useState([]);

    const handleClose = () => {
        setShow(false);
        setSelectedPlan(null);
        setBillingType("monthly");
    };
    const handleShow = () => setShow(true);

    const getPlanList = async () => {
        loadingShow();
        let resp = await getApi(urlApi.getChatPlanList);
        loadingHide();
        if (resp?.responseCode === 200) {
            // Make sure each plan has operatorCount (fallback to 1 if missing)
            const list = resp?.data?.map((plan) => ({
                ...plan,
                operatorCount: plan.operatorCount || 1,
            }));
            setPlanList(list);
            handleShow();
        } else {
            setPlanList([]);
            toast.error(resp?.message);
        }
    };

    const assign = async () => {
        if (!selectedPlan) return;

        let postData = {
            "userId": userData?._id,
            "organizationId": userData?.defaultOrganizationId,
            "transactionId": document.getElementById("transaction-id").value?.trim(),
            "planId": billingType === "monthly" ? selectedPlan?.monthlyId : selectedPlan?.yearlyId,
            "operatorCount": selectedPlan?.operatorCount
        }

        let resp = await postApi(postData, urlApi.assignChatPlanToUser);
        if (resp?.responseCode === 200) {
            toast.success(resp?.message);
            handleClose();
        } else {
            toast.error(resp?.message);
        }
    };

    // update operator count inside selectedPlan
    const updateOperatorCount = (value) => {
        if (!selectedPlan) return;
        const updatedCount = Math.max(1, selectedPlan.operatorCount + value);
        const updatedPlan = { ...selectedPlan, operatorCount: updatedCount };

        setSelectedPlan(updatedPlan);

        // also update planList state so dropdown stays in sync
        setPlanList((prev) =>
            prev.map((plan) =>
                (billingType === "monthly" ? plan.monthlyId : plan.yearlyId) ===
                    (billingType === "monthly" ? selectedPlan.monthlyId : selectedPlan.yearlyId)
                    ? updatedPlan
                    : plan
            )
        );
    };

    return (
        <>
            <button
                onClick={() => getPlanList()}
                className="p-2 rounded-md hover:bg-gray-100 transition"
            >
                <IconMessageCircleStar size={20} className="text-gray-700" />
            </button>

            <Modal show={show} onHide={handleClose} backdrop="static" size="lg" centered>
                <Modal.Body className="rounded-xl p-6 bg-white shadow-lg">
                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-800">
                            Assign Chat Plan
                        </h2>
                        <IconX
                            className="cursor-pointer text-gray-500 hover:text-red-500 transition"
                            onClick={handleClose}
                        />
                    </div>

                    <hr className="my-4 border-gray-200" />

                    <div className="flex flex-col gap-6">

                        {/* transaction ID input */}
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-2" htmlFor="transaction-id">
                                Transaction ID
                            </label>
                            <input type="text" className="w-full px-3 py-2 border rounded-md text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" id="transaction-id" />
                        </div>

                        {/* Billing Toggle */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-2">
                                Billing Type
                            </p>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setBillingType("monthly")}
                                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${billingType === "monthly"
                                        ? "bg-primary text-white border-primary"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setBillingType("yearly")}
                                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${billingType === "yearly"
                                        ? "bg-primary text-white border-primary"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                >
                                    Yearly
                                </button>
                            </div>
                        </div>

                        {/* Plan Dropdown */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-2">
                                Select Plan
                            </p>
                            <div className="relative">
                                <select
                                    value={
                                        selectedPlan
                                            ? billingType === "monthly"
                                                ? selectedPlan.monthlyId
                                                : selectedPlan.yearlyId
                                            : ""
                                    }
                                    onChange={(e) => {
                                        const plan = planList.find(
                                            (p) =>
                                                (billingType === "monthly"
                                                    ? p.monthlyId
                                                    : p.yearlyId) === e.target.value
                                        );
                                        setSelectedPlan(plan);
                                    }}
                                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
                                >
                                    <option value="" disabled>
                                        Choose a plan
                                    </option>
                                    {planList?.map((data, index) => (
                                        <option
                                            key={index}
                                            value={
                                                billingType === "monthly"
                                                    ? data.monthlyId
                                                    : data.yearlyId
                                            }
                                        >
                                            {data?.name}
                                        </option>
                                    ))}
                                </select>
                                <IconChevronDown
                                    size={18}
                                    className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                                />
                            </div>
                        </div>

                        {/* Operator Count */}
                        {selectedPlan && (
                            <div>
                                <p className="text-sm font-semibold text-gray-700 mb-2">
                                    Operators
                                </p>
                                <div className="flex items-center gap-3">
                                    <button
                                        className="px-3 py-1 rounded-md border bg-gray-50 hover:bg-gray-100 transition"
                                        onClick={() => updateOperatorCount(-1)}
                                    >
                                        -
                                    </button>
                                    <span className="px-4 py-1 border rounded-md text-gray-800">
                                        {selectedPlan.operatorCount}
                                    </span>
                                    <button
                                        className="px-3 py-1 rounded-md border bg-gray-50 hover:bg-gray-100 transition"
                                        onClick={() => updateOperatorCount(1)}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>

                    <hr className="my-4 border-gray-200" />

                    {/* Footer */}
                    <div className="flex justify-end gap-3">
                        <button
                            className="px-5 py-2 border rounded-md text-gray-600 hover:bg-gray-100 transition"
                            onClick={handleClose}
                        >
                            Close
                        </button>
                        <button
                            className="px-5 py-2 rounded-md bg-primary text-white hover:bg-orange-600 transition"
                            onClick={assign}
                            disabled={!selectedPlan}
                        >
                            Assign
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default AssignChatPlanModal;
